import {
  AdjustmentsHorizontalIcon,
  ArchiveBoxIcon,
  BanknotesIcon,
  BeakerIcon,
  BookmarkIcon,
  BuildingOfficeIcon,
  CalendarDaysIcon,
  ChartBarIcon,
  ClipboardDocumentCheckIcon,
  ClockIcon,
  CloudIcon,
  CubeIcon,
  DocumentTextIcon,
  HashtagIcon,
  MapPinIcon,
  ShoppingCartIcon,
  SparklesIcon,
  Squares2X2Icon,
  TagIcon,
  TicketIcon,
  TruckIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import type { ComponentType } from 'react';

export type SidebarAccess = 'authenticated' | 'catalog' | 'orders' | 'partners' | 'revenuePlanning' | 'agents' | 'agentAudit' | 'agentDatasets' | 'agentOperations';

export interface SidebarNavItem {
  id: string;
  label: string;
  to: string;
  icon: ComponentType<{ className?: string }>;
  access?: SidebarAccess;
  activePaths?: string[];
  comingSoon?: boolean;
}

export interface SidebarNavGroup {
  id: string;
  label: string;
  to: string;
  icon: ComponentType<{ className?: string }>;
  access?: SidebarAccess;
  activePaths?: string[];
  children: SidebarNavItem[];
}

export const workspaceNav: SidebarNavItem[] = [
  { id: 'dashboard', label: 'Dashboard', to: '/dashboard', icon: ChartBarIcon },
  { id: 'forecast', label: 'Forecast', to: '/revenue-planning', icon: BanknotesIcon, access: 'revenuePlanning' },
  { id: 'tasks', label: 'My Tasks', to: '/tasks', icon: ClipboardDocumentCheckIcon },
];

export const crmGroup: SidebarNavGroup = {
  id: 'crm',
  label: 'CRM',
  to: '/clients',
  icon: BuildingOfficeIcon,
  activePaths: ['/clients', '/accounts/create', '/leads', '/prospects', '/tickets', '/meetings', '/partners'],
  children: [
    {
      id: 'clients',
      label: 'Clients / Accounts',
      to: '/clients',
      icon: BuildingOfficeIcon,
      activePaths: ['/clients', '/accounts/create'],
    },
    { id: 'leads', label: 'Leads', to: '/leads', icon: UserGroupIcon },
    { id: 'prospects', label: 'Prospects', to: '/prospects', icon: UserGroupIcon },
    { id: 'tickets', label: 'Tickets', to: '/tickets', icon: TicketIcon },
    { id: 'meetings', label: 'Meetings', to: '/meetings', icon: CalendarDaysIcon },
    { id: 'partners', label: 'Partners', to: '/partners', icon: UserGroupIcon, access: 'partners' },
  ],
};

export const operationsNav: SidebarNavItem[] = [
  { id: 'orders', label: 'Orders', to: '/orders', icon: ShoppingCartIcon, access: 'orders' },
];

export const cre8orAiNav: SidebarNavItem[] = [
  { id: 'agent-inbox', label: 'Agent Inbox', to: '/agents/inbox', icon: SparklesIcon, access: 'agents' },
  { id: 'agent-prompts', label: 'Agent Prompts', to: '/agents/prompts', icon: DocumentTextIcon, access: 'agents' },
  { id: 'agent-knowledge', label: 'Agent Knowledge', to: '/agents/knowledge', icon: BookmarkIcon, access: 'agents' },
  { id: 'agent-audits', label: 'Agent Audits', to: '/agents/audits', icon: ClipboardDocumentCheckIcon, access: 'agentAudit' },
  { id: 'agent-datasets', label: 'Agent Datasets', to: '/agents/datasets', icon: ArchiveBoxIcon, access: 'agentDatasets' },
  { id: 'agent-operations', label: 'Embedding Status', to: '/agents/operations', icon: AdjustmentsHorizontalIcon, access: 'agentOperations' },
];

export const catalogGroup: SidebarNavGroup = {
  id: 'catalog',
  label: 'Catalog',
  to: '/catalog',
  icon: BeakerIcon,
  access: 'catalog',
  activePaths: ['/catalog'],
  children: [],
};

export const generatedSkusGroup: SidebarNavGroup = {
  id: 'generated-skus',
  label: 'Generated SKUs',
  to: '/catalog/generated-skus',
  icon: TagIcon,
  access: 'catalog',
  activePaths: [
    '/catalog/generated-skus',
    '/catalog/bases',
    '/catalog/actives',
    '/catalog/fragrances',
    '/catalog/packaging-sets',
    '/catalog/packaging',
    '/catalog/labels',
    '/catalog/sku-history',
  ],
  children: [
    { id: 'bases', label: 'Bases', to: '/catalog/bases', icon: BeakerIcon },
    { id: 'actives', label: 'Actives', to: '/catalog/actives', icon: SparklesIcon },
    { id: 'fragrances', label: 'Fragrances', to: '/catalog/fragrances', icon: CloudIcon },
    { id: 'labels', label: 'Labels', to: '/catalog/labels', icon: BookmarkIcon },
    { id: 'sku-history', label: 'SKU History', to: '/catalog/sku-history', icon: ClockIcon },
  ],
};

export const packagingSetsGroup: SidebarNavGroup = {
  id: 'packaging-sets',
  label: 'Packaging Sets',
  to: '/catalog/packaging-sets',
  icon: Squares2X2Icon,
  access: 'catalog',
  activePaths: ['/catalog/packaging-sets', '/catalog/packaging'],
  children: [
    { id: 'packaging', label: 'Packaging', to: '/catalog/packaging', icon: ArchiveBoxIcon },
  ],
};

export const catalogStandaloneItems: SidebarNavItem[] = [
  { id: 'image-gallery', label: 'Image Gallery', to: '/catalog/files', icon: DocumentTextIcon },
  { id: 'suppliers', label: 'Suppliers', to: '/catalog/suppliers', icon: TruckIcon },
];

export const referenceDataGroup: SidebarNavGroup = {
  id: 'reference-data',
  label: 'Reference Data',
  to: '/catalog/concern-tags',
  icon: AdjustmentsHorizontalIcon,
  access: 'catalog',
  activePaths: [
    '/catalog/concern-tags',
    '/catalog/locations',
    '/catalog/categories',
    '/catalog/package-types',
    '/catalog/fragrance-types',
  ],
  children: [
    { id: 'concern-tags', label: 'Concern Tags', to: '/catalog/concern-tags', icon: HashtagIcon },
    { id: 'locations', label: 'Locations', to: '/catalog/locations', icon: MapPinIcon },
    { id: 'categories', label: 'Categories', to: '/catalog/categories', icon: Squares2X2Icon },
    { id: 'package-types', label: 'Package Types', to: '/catalog/package-types', icon: CubeIcon },
    { id: 'fragrance-types', label: 'Fragrance Types', to: '/catalog/fragrance-types', icon: CloudIcon },
  ],
};

export function isPathActive(pathname: string, item: Pick<SidebarNavItem, 'to' | 'activePaths'>): boolean {
  const paths = item.activePaths ?? [item.to];
  return paths.some((path) => pathname === path || pathname.startsWith(`${path}/`));
}
